﻿/**
 * 真正的 iframe 綁定。對應 docs/wire.md §9。
 *
 * 這是整個系統裡唯一知道 iframe 存在的地方。Bus 與 HostEndpoint 都不知道。
 */

import { HostEndpoint, WIRE_MAGIC, isFrame, messagePortAdapter } from '@qdesktop/kernel'
import type { GesturePhase, GesturePoint } from '@qdesktop/kernel'
import type { Bus, BusError } from '@qdesktop/kernel'

export interface IframeBindingOptions {
  bus: Bus
  iframe: HTMLIFrameElement
  uid: string
  /** App 的確切來源。**不得**使用 "*"。 */
  appOrigin: string
  onFault?: (error: BusError) => void
  /** 每次（重新）連上時呼叫。重載時會再次觸發。 */
  onConnect?: (attempt: number) => void
  /** App 回報有人碰了它的畫面。見 wire.md 的 `touch`。 */
  onTouch?: () => void
  /** App 把一次兩指手勢讓給桌面。見 wire.md 的 `gesture`。 */
  onGesture?: (phase: GesturePhase, points: readonly GesturePoint[]) => void
}

export class IframeBinding {
  private readonly options: IframeBindingOptions
  private endpoint: HostEndpoint | null = null
  private attempts = 0
  private disposed = false
  private readonly listener: (event: MessageEvent) => void

  private constructor(options: IframeBindingOptions) {
    this.options = options
    this.listener = (event) => this.onWindowMessage(event)
  }

  static bind(options: IframeBindingOptions): IframeBinding {
    const binding = new IframeBinding(options)
    window.addEventListener('message', binding.listener)
    return binding
  }

  private onWindowMessage(event: MessageEvent): void {
    if (this.disposed) return
    if (!isFrame(event.data) || event.data.t !== 'ready') return

    // §9 建立時的必要檢查：訊息確實來自這個 iframe
    if (event.source !== this.options.iframe.contentWindow) return

    this.handover()
  }

  /**
   * 交出一條新的 MessageChannel。
   *
   * iframe 重載（平板記憶體回收之後的再水合）時會再次收到 ready，
   * 此時舊的 Port 已死，直接換一條新的——這就是 §8.3 的重載流程。
   */
  private handover(): void {
    const { bus, iframe, uid, appOrigin, onFault, onTouch, onGesture } = this.options

    this.endpoint?.close('iframe 重新載入')

    const channel = new MessageChannel()
    this.endpoint = HostEndpoint.attach({
      bus,
      port: messagePortAdapter(channel.port1),
      uid,
      ...(onFault ? { onFault } : {}),
      ...(onTouch ? { onTouch } : {}),
      ...(onGesture ? { onGesture } : {}),
    })

    // targetOrigin 必須是確切來源，不得用 "*"
    iframe.contentWindow?.postMessage({ qd: WIRE_MAGIC, t: 'port' }, appOrigin, [channel.port2])

    this.attempts += 1
    this.options.onConnect?.(this.attempts)
  }

  dispose(reason = '視窗已關閉'): void {
    if (this.disposed) return
    this.disposed = true
    window.removeEventListener('message', this.listener)
    this.endpoint?.close(reason)
    this.endpoint = null
  }
}
